import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { UiService } from './ui.service';
import { SimulationInfo } from '@classes/simulation/simulation-info';
import { Simulation } from '@classes/simulation/simulation';
import { Partition } from '@classes/simulation/partition';
import { SimulationData } from '@classes/simulation/simulation-data';
import { ProjectionRate } from '@classes/simulation/projection-rate';
import { CalculatorService } from './calculator.service';
import { UserRole } from '@classes/licence/user-role';
import { User } from '@classes/licence/user';
import { Partitioning } from '@classes/partitioning';
import { ReportData } from '@classes/report/report-data';
import { SimulationFlagType } from '@enums/simulation-flag-type.enum';

@Injectable({
    providedIn: 'root'
})
export class SimulationService {
    selectedSimulationChanged   = new Subject<Simulation>();
    selectedSimulationsSubject  = new BehaviorSubject<Simulation[]>(null);
    simulationInfosListChanged  = new Subject<SimulationInfo[]>();
    partitioningListChanged     = new Subject<Partitioning[]>();
    selectedYearChanged         = new Subject<number>();
    selectedPartitionChanged    = new Subject<Partition>();
    simulationValChanged        = new Subject<SimulationData>();
    projectionRateChanged       = new Subject<ProjectionRate>();

    public referenceYear?: number = null;

    private baseUrl = environment.apiUrl + 'simulations';
    private simulation:     Simulation;
    private simulationBkp:  Simulation;
    private selectedPartition: Partition;

    constructor(
        private uiSrv: UiService,
        private http: HttpClient,
        private router: Router,
        private calcSrv: CalculatorService
    ) { }

    getInfoList(): Observable<SimulationInfo[]> {
        this.simulationInfosListChanged.next(null);

        return this.http.get<SimulationInfo[]>(this.baseUrl)
        .pipe(
            tap(simulations => {
                this.simulationInfosListChanged.next([ ... simulations ]);
            })
        );
    }

    getPartitioningsList(): Observable<Partitioning[]> {
        this.partitioningListChanged.next(null);

        const httpParams = new HttpParams()
            .append("byPartitioning", "true");

        return this.http.get<Partitioning[]>(this.baseUrl, {params: httpParams})
        .pipe(
            tap(parts => {
                parts.map(p => {
                    p.simulations.map(s => {
                        // todo : récupérer la valeur par défaut depuis le back
                        s.years
                        if (s.createdAt && new Date(s.createdAt).getFullYear() < 100)
                            s.createdAt = undefined;
                        if (s.years && s.years.length > 2)
                            s.to = s.years[s.years.length - 3];
                    });
                });
                this.partitioningListChanged.next([ ... parts ]);
            })
        );
    }

    get(code?: string, to?: number): Observable<Simulation> {
        this.uiSrv.routeLoadingStateChanged.next(true);

        const url = this.baseUrl + (code ? `/${code}` : '/last');

        let httpParams = new HttpParams()
            .append("hierarchical", "false");

        if (to) {
            httpParams = httpParams
                .append("to", to.toString());
        }

        return this.http.get<Simulation>(url, {params: httpParams})
        .pipe(
            tap(simuFromApi => {
                this.setSimulation(simuFromApi);
                this.simulation.endDate = to;
                this.simulationBkp = JSON.parse(JSON.stringify(simuFromApi));

                this.selectedSimulationChanged.next({...simuFromApi});
                this.uiSrv.routeLoadingStateChanged.next(false);
            })
        );
    }

    getList(codes: string[], year: number = null): Observable<ReportData> {
        if (!year) {
            year = this.referenceYear;
        }

        this.selectedSimulationsSubject.next(null);

        let httpParams = new HttpParams();

        codes.map(code => {
            httpParams = httpParams.append("codes", code);
        });

        if (year) {
            httpParams = httpParams.append("referenceYear", year.toString());
        }
        return this.http.get<ReportData>(`${this.baseUrl}/q`, { params: httpParams })
        .pipe(
            tap(response => {
                this.selectedSimulationsSubject.next([ ...response.simulations ]);
            })
        );
    }

    delete(code: string): void {
        this.uiSrv.loadingStateChanged.next(true);

        this.http.delete<Simulation>(this.baseUrl + `/${code}`)
        .subscribe(() => {
            this.uiSrv.loadingStateChanged.next(false);

            if(code === this.simulation.code) {
                this.get().subscribe();
            }

            this.getPartitioningsList().subscribe();
            this.uiSrv.showSnackbar("main.dialogs.simulationDeleted.fullLabel", true);
        });
    }

    calculate(simulationToCalc: Simulation): void {
        this.uiSrv.routeLoadingStateChanged.next(true);

        const httpParams = new HttpParams()
            .append("hierarchical", "false");

        this.http.put<Simulation>(this.baseUrl + '/calculate', simulationToCalc, {params: httpParams})
        .subscribe(simuFromApi => {
            this.setSimulation(simuFromApi);

            this.uiSrv.routeLoadingStateChanged.next(false);
            this.selectedSimulationChanged.next({...simuFromApi});
            this.router.navigate(['/simulation/live']);
        });
    }

    calculateLocal(editedPartCode: string): void {
        // Edited by reference
        this.calcSrv.calculate(this.simulation, editedPartCode);
    }

    post(simulationToSave: Simulation): void {
        this.uiSrv.routeLoadingStateChanged.next(true);

        const httpParams = new HttpParams()
            .append("hierarchical", "false");

        this.http.post<Simulation>(this.baseUrl, simulationToSave, {params: httpParams})
        .subscribe(simuFromApi => {
            this.uiSrv.routeLoadingStateChanged.next(false);
            this.setSimulation(simuFromApi);
            this.simulationBkp = JSON.parse(JSON.stringify(simuFromApi));

            this.selectedSimulationChanged.next({...simuFromApi});
            // Get updated list from API
            this.getPartitioningsList().subscribe();
            this.uiSrv.showSnackbar("main.dialogs.simulationSaved.fullLabel", true);
        });
    }

    put(simulationToSave: Simulation): void {
        this.uiSrv.routeLoadingStateChanged.next(true);

        const httpParams = new HttpParams()
            .append("hierarchical", "false");

        this.http.put<Simulation>(this.baseUrl, simulationToSave, {params: httpParams})
        .subscribe(simuFromApi => {
            this.uiSrv.routeLoadingStateChanged.next(false);
            this.setSimulation(simuFromApi);
            this.simulationBkp = JSON.parse(JSON.stringify(simuFromApi));

            this.selectedSimulationChanged.next({...simuFromApi});
            // Get updated list from API
            this.getPartitioningsList().subscribe();
            this.uiSrv.showSnackbar("main.dialogs.simulationSaved.fullLabel", true);
        });
    }

    share(datatoSend: {roles: UserRole[], users: User[]}, simulationCode: string): Observable<string> {
        this.uiSrv.loadingStateChanged.next(true);

        return this.http.post<string>(this.baseUrl + `/${simulationCode}/share`, datatoSend)
        .pipe(
            tap(result => {
                this.uiSrv.loadingStateChanged.next(false);
                this.uiSrv.showSnackbar("main.dialogs.simulationShared.fullLabel", true);
            })
        );
    }

    get selectedCode(): string {
        return this.simulation.code;
    }

    get localSimu(): Simulation {
        return this.simulation;
    }

    get backupSimu(): Simulation {
        return this.simulationBkp ? JSON.parse(JSON.stringify(this.simulationBkp)) : null;
    }

    get selPartition(): Partition {
        return this.selectedPartition;
    }

    setSelectedPartition(partition: Partition): void {
        this.selectedPartition = partition;
    }

    resetSimulation(): void {
        this.simulation = JSON.parse(JSON.stringify(this.simulationBkp));
        this.selectedSimulationChanged.next(this.simulation);
    }

    clearLocal(): void {
        if(this.simulation) {
            this.simulation = null;
            this.selectedPartition = null;
            this.selectedSimulationChanged.next(null);
            this.selectedPartitionChanged.next(null);
        }
    }

    private setSimulation(simulation: Simulation): void {
        // store in RAM for future use
        this.simulation = simulation;
        // TODO : stocker le flag en db ?
        this.simulation.partitions.map(p => {
            p.simulationValues.years.map(y => {
                y.isAmountEdited = y.flag === SimulationFlagType.modified && y.isRateNeutralized && y.additionalAmount !== 0;
            });
        });
    }
}
